/* About page — `.lpv3` redesign (matches landing/reseller styling). */
import { Lpv3Header, Lpv3Footer } from "@/components/Lpv3Chrome";
import { useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import "./LandingPageV3.css";

const JAGRUTI_IMG = "https://customer-assets.emergentagent.com/job_92507c97-3084-44a2-9664-771f9d4ef69a/artifacts/zgsfhjk4_CYNO9942.jpg";
const RONAK_IMG = "https://customer-assets.emergentagent.com/job_92507c97-3084-44a2-9664-771f9d4ef69a/artifacts/hjfcg8iy_CYNO9765.jpg";

const PILLARS = [
  { icon: "ti-eye", h: "Radical Transparency", d: "Decrypting complex DRHPs, SEBI regulations and compliance filings — turning opaque legal jargon into actionable intelligence every founder can understand." },
  { icon: "ti-brain", h: "AI-First Integrity", d: "Using data, not intuition, to drive capital market decisions. From automated DRHP extraction to AI-powered readiness scoring, every insight is evidence-based." },
  { icon: "ti-heart", h: "Founder-Centricity", d: "Built by founders, for founders. Every feature in IPOLabs exists because a real entrepreneur hit a real wall in their listing journey." },
];

const AboutPage = () => {
  const navigate = useNavigate();
  return (
    <div className="lpv3" data-testid="about-page">
      <Seo
        title="About IPOLabs | Founder-Led India IPO Platform"
        description="About IPOLabs — a founder-led mission to remove the black box from the Indian IPO journey with one transparent, unified platform."
        path="/about"
      />
      <Lpv3Header />

      {/* HERO */}
      <div className="hero compact" data-testid="about-hero">
        <span className="hero-pill">About IPOLabs</span>
        <div className="hero-h">Democratizing <span className="accent">Capital Markets</span></div>
        <div className="hero-sub hero-sub-wide">For too long, India&apos;s IPO ecosystem operated behind closed doors. IPOLabs is building India&apos;s first AI-powered IPO Operating System — making the primary market transparent, data-driven, and radically accessible for every entrepreneur.</div>
      </div>

      {/* FOUNDERS */}
      <div className="sec">
        <div className="sec-kick">The Duo</div>
        <div className="sec-h">Meet the <span className="accent">Founders</span></div>
        <div className="sec-d">Regulatory rigor meets technological speed — the bridge between the rulebook and the runway.</div>
        <div className="ab-founders">
          <div className="ab-founder" data-testid="founder-card-jagruti">
            <div className="ab-founder-top">
              <img src={JAGRUTI_IMG} alt="CA Jagruti Sahu" />
              <div>
                <div className="ab-kick">Guardian of Trust &amp; Compliance</div>
                <div className="ab-fname">CA Jagruti Sahu</div>
                <div className="ab-frole">Co-Founder &amp; Director</div>
              </div>
            </div>
            <div className="ab-fbio">A Chartered Accountant forged in Indian financial compliance, Jagruti brings deep expertise across corporate governance, SEBI regulatory frameworks, and audit-grade precision. She doesn&apos;t just understand the rules — she anticipates how SEBI interprets them.</div>
            <div className="ab-tags">
              {["SEBI ICDR", "Corporate Governance", "Financial Audits", "DRHP Compliance", "Risk Assessment"].map((t) => <span key={t} className="ab-tag">{t}</span>)}
            </div>
          </div>
          <div className="ab-founder" data-testid="founder-card-ronak">
            <div className="ab-founder-top">
              <img src={RONAK_IMG} alt="Ronak Rajan" />
              <div>
                <div className="ab-kick">Architect of Innovation</div>
                <div className="ab-fname">Ronak Rajan</div>
                <div className="ab-frole">Co-Founder &amp; Director</div>
              </div>
            </div>
            <div className="ab-fbio">A tech-driven entrepreneur and B2B strategist, Ronak builds at the intersection of AI and enterprise SaaS. He turns complex regulatory workflows into products people want to use — because compliance software should feel like magic, not paperwork.</div>
            <div className="ab-tags">
              {["AI / ML", "B2B SaaS", "Platform Architecture", "Vibe Coding", "Go-to-Market"].map((t) => <span key={t} className="ab-tag">{t}</span>)}
            </div>
          </div>
        </div>
        <div className="ab-bridge">
          <span><i className="ti ti-shield-check" aria-hidden="true" /> Regulatory Rigor &nbsp;·&nbsp; Technological Speed <i className="ti ti-bolt" aria-hidden="true" /></span>
        </div>
      </div>

      {/* ORIGIN STORY */}
      <div className="sec alt">
        <div className="sec-kick">The Origin Story</div>
        <div className="sec-h">When Compliance Met Code</div>
        <div className="sec-d" style={{ maxWidth: 720 }}>
          A tech founder and a financial whiz — facing the same regulatory friction from two opposite ends of the table. One watched brilliant founders lose months to manual DRHP drafts, opaque SEBI workflows, and compliance filings that felt designed to exhaust them. The other saw the AI breakthrough — the quiet inflection point where these workflows could be structured, automated, and made intelligible in days, not quarters. They built IPOLabs at the intersection of regulatory rigor and technological speed.
        </div>
      </div>

      {/* MISSION + PILLARS */}
      <div className="sec">
        <div className="ab-mission">
          <div className="ab-kick">Our Mission</div>
          <h3>To bridge the IPO readiness gap for every Indian SME — with AI precision and regulatory integrity.</h3>
          <p>Building infrastructure that makes going public as systematic as running a SaaS business — measurable, data-driven, and accessible from Day 1.</p>
        </div>
        <div className="vals">
          {PILLARS.map((p, i) => (
            <div className="val" key={i} data-testid={`about-pillar-${i}`}>
              <div className="val-ic"><i className={`ti ${p.icon}`} aria-hidden="true" /></div>
              <div className="val-h">{p.h}</div>
              <div className="val-d">{p.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="cta" data-testid="about-cta">
        <div className="cta-h">Ready to start your IPO journey?</div>
        <div className="cta-d">Whether you&apos;re a first-generation entrepreneur or a seasoned CFO, IPOLabs gives you the tools to navigate the listing process with confidence.</div>
        <div className="hero-btns">
          <button className="btn-dark" onClick={() => navigate("/login")} data-testid="about-cta-get-started"><i className="ti ti-rocket" aria-hidden="true" /> Get Started</button>
          <button className="btn-line" onClick={() => navigate("/careers")} data-testid="about-cta-careers">Join the Team</button>
        </div>
      </div>

      <Lpv3Footer />
    </div>
  );
};

export default AboutPage;
